import React from 'react';
import { 
  Wrench, 
  ArrowRight, 
  Brain, 
  Network, 
  Shield, 
  Gauge 
} from 'lucide-react';
import HeroImage from '../../components/HeroImage';

function CustomSolutions() {
  const GOOGLE_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLScVJ5NP38k3xJuhVYWnZSFm8PKrzA6Dqu7U73X9fdCI5A2PtQ/viewform?usp=header";

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen mx-[4.2mm] mt-[4.2mm]">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#00ffff]/10 via-transparent to-[#ff0000]/10 animate-gradient z-10 rounded-lg"></div>
          <div className="absolute inset-0 rounded-lg overflow-hidden">
            <HeroImage 
              src="https://images.unsplash.com/photo-1452860606245-08befc0ff44b" 
              alt="Traditional wood carving craftsmanship"
              priority={true}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50 rounded-lg z-20"></div>
        </div>

        <div className="relative h-full flex items-center z-30">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-center mb-8">
              <Wrench className="h-16 w-16 text-[#ff0000]" />
            </div>
            <h1 className="text-5xl font-bold text-center text-white mb-6">Custom Solutions</h1>
            <p className="text-xl text-white/80 text-center max-w-3xl mx-auto">
              Every business has its own unique challenges. We source, create and combine solutions to clear your path to success.
            </p>
          </div>
        </div>
      </section>

      {/* Solutions Grid */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-white mb-12">Why Trust Atlas AI for Custom Solutions?</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="white-holographic-card p-8 rounded-lg">
            <div className="mb-6">
              <Brain className="h-12 w-12 text-[#ff0000]" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-6">Custom AI Development</h3>
            <p className="text-gray-600">
              We start by listening to you. Through an in-depth consultation, we uncover your unique challenges and explore opportunities where AI can make the biggest impact. From there, we design and build bespoke AI tools and strategies that don't just solve problems but align perfectly with your goals.
            </p>
          </div>

          <div className="white-holographic-card p-8 rounded-lg">
            <div className="mb-6">
              <Network className="h-12 w-12 text-[#ff0000]" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-6">System Integration</h3>
            <p className="text-gray-600">
              Our journey doesn't end when your solution goes live. We'll be by your side with ongoing support, continuous optimization, and hands-on collaboration to ensure your AI systems evolve with your business. No generic solutions here—everything we do is as unique as you are.
            </p>
          </div>

          <div className="white-holographic-card p-8 rounded-lg">
            <div className="mb-6">
              <Shield className="h-12 w-12 text-[#ff0000]" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-6">Secure Innovation</h3>
            <p className="text-gray-600">
              Your trust matters to us, which is why we prioritize security at every step. From enterprise-grade protection to regular audits and updates, we ensure your data stays safe and compliant. With secure API integrations and endpoints, you can focus on innovation without worry.
            </p>
          </div>

          <div className="white-holographic-card p-8 rounded-lg">
            <div className="mb-6">
              <Gauge className="h-12 w-12 text-[#ff0000]" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-6">Performance Optimization</h3>
            <p className="text-gray-600">
              We don't just set it and forget it. With real-time monitoring, automated scaling, and continuous improvement algorithms, we ensure your AI solutions stay efficient, effective, and always one step ahead. Every enhancement is designed to deliver peak performance and measurable results.
            </p>
          </div>
        </div>
      </div>

      {/* Process Timeline */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center text-white mb-16">Our Journey Together</h2>
        <div className="grid md:grid-cols-4 gap-8">
          <TimelineStep
            number="01"
            title="Listen"
            description="We start by understanding your unique story and vision"
          />
          <TimelineStep
            number="02"
            title="Design"
            description="Together, we craft the perfect solution for your needs"
          />
          <TimelineStep
            number="03"
            title="Create"
            description="We bring your vision to life with care and expertise"
          />
          <TimelineStep
            number="04"
            title="Grow"
            description="We support your journey with ongoing partnership"
          />
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 py-24">
        <div className="white-holographic-card p-12 rounded-2xl text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Ready to Take This Challenge Together?</h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Let's get together and see how we can achieve the impossible.
          </p>
          <a
            href={GOOGLE_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-4 bg-[#ff0000] text-white rounded-lg hover:bg-[#00ffff] transition-colors font-semibold inline-flex items-center mx-auto"
          >
            Let's Do It
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </div>
      </div>
    </div>
  );
}

function TimelineStep({ number, title, description }) {
  return (
    <div className="text-center p-6">
      <div className="text-4xl font-bold text-[#ff0000] mb-4">{number}</div>
      <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
      <p className="text-white/80">{description}</p>
    </div>
  );
}

export default CustomSolutions;